import { CardModel } from "../../../swagger/api.client.generated";
import { CardVariant, SafetyCardCategory } from "./card.models";

// The DB sentinel for an unnamed/unedited custom card (set in the CardSubtypeTables migration,
// and stamped locally for editable cards that arrive with an empty name — see card.service.ts).
// An empty/absent name is treated the same as the sentinel.
export const BLANK_CARD_NAME = "blank";
export function isBlankCardName(name: string | null | undefined): boolean {
  return !name || name === BLANK_CARD_NAME;
}

// Maps SafetyCardCategoryId → SafetyCardCategory.
// The subtype model uses IDs 1-6 (legacy 9-14 were re-IDed 9→1 … 14→6 in Migration1,
// ordered by the old CardCategoryId).
const SAFETY_CATEGORY_MAP: Record<number, SafetyCardCategory> = {
  1: SafetyCardCategory.Feelings,
  2: SafetyCardCategory.Thoughts,
  3: SafetyCardCategory.Novelty_And_Play,
  4: SafetyCardCategory.Body,
  5: SafetyCardCategory.Relationships,
  6: SafetyCardCategory.Experiences,
};

// SafetyCardCategory → human-readable label, for the "Card type" field in the create/edit dialog.
const SAFETY_CATEGORY_LABELS: Record<SafetyCardCategory, string> = {
  [SafetyCardCategory.Feelings]: "Feelings",
  [SafetyCardCategory.Thoughts]: "Thoughts",
  [SafetyCardCategory.Novelty_And_Play]: "Novelty & Play",
  [SafetyCardCategory.Body]: "Body",
  [SafetyCardCategory.Relationships]: "Relationships",
  [SafetyCardCategory.Experiences]: "Experiences",
};
export function getSafetyCategoryLabel(category: SafetyCardCategory | null | undefined): string | null {
  return category ? SAFETY_CATEGORY_LABELS[category] ?? null : null;
}

// Translates raw CardModel (current API schema) into a semantic CardVariant.
// This is the only function that needs to change when the DB schema changes.
// Future: when separate ActivityCards/SafetyCards/UserActivityCards/UserSafetyCards tables
// are exposed by the API, update this function to derive isCustom from the API source
// rather than cardDeckId. Zero component changes needed.
export function resolveCardVariant(
  card: CardModel | null | undefined,
  isPlaceholderEmpty: boolean
): CardVariant {
  if (isPlaceholderEmpty) return { kind: "empty" };
  if (!card) return { kind: "placeholder" };

  // Plan-loaded cards return cardDeckId=0 (uninitialized int) and put the real
  // deck ID in cardDeckType.cardDeckId. Use || so the literal 0 also falls back.
  const deckId     = card.cardDeckId || card.cardDeckType?.cardDeckId;
  const categoryId = card.cardCategory?.cardCategoryId;
  const isCustom   = deckId === 3 || card.isUserDefined || card.isEditableTemplate || (!deckId && isBlankCardName(card.cardName)); // Deck 3, user-edited, or pre-edit template = editable

  // Activity-only category IDs (Yellow variants): 1 = recommended, 7 = mandatory.
  // Everything else from Deck 3 is safety (null category = legacy Grey / no category).
  const isActivityOnlyCategory = categoryId === 1 || categoryId === 7;

  // Safety deck (Angel Deck = 2), or Deck 3 without an activity-specific category
  if (deckId === 2 || (deckId === 3 && !isActivityOnlyCategory)) {
    const category = SAFETY_CATEGORY_MAP[categoryId] ?? null; // null = legacy Grey (cat ID 8 or absent)
    return { kind: "safety", category, isCustom };
  }

  // Activity deck (Activity Deck = 1), or Deck 3 activity-style card (categoryId 1 or 7)
  if (deckId === 1 || deckId === 3) {
    return { kind: "activity", isMandatory: isMandatoryActivityCard(card), isCustom };
  }

  return { kind: "placeholder" };
}

// Mandatory activity cards (DB IsMandatory flag, or legacy categoryId 7) are anchor cards that
// can't be soft-deleted from the user's library. Shared by the picker's delete guard and the
// card-shell DELETE-item gate so the rule stays in one place.
export function isMandatoryActivityCard(card: CardModel | null | undefined): boolean {
  if (!card) return false;
  return (card as any).isMandatory ?? card.cardCategory?.cardCategoryId === 7;
}

// Body of the library "delete this card?" confirmation, shared by the card picker and the activity /
// star card dialogs so the copy lives in one place. `inUse` is the card-delete safeguard answer
// (a favourite plan, a hand or a Favourite hand still uses the card, FLIP-607).
export function cardDeleteConfirmBody(inUse: boolean): string {
  return inUse
    ? "This card is still in use in your hands, favourite hands or favourite plans. Deleting it only removes it from your library: hands and favourite hands keep their saved copy. Favourite plans will ask whether to restore it to your deck or remove it the next time you reuse them.\n\nAre you sure you want to delete this card?"
    : "Are you sure you want to delete this card?";
}

// A custom card the user hasn't personalised yet: it's an editable template placeholder that has
// not been turned into a real user-defined card. Same signal used by the flare-up/safety plan
// local services to drop temporary template cards.
export function isTemplateCustomCard(card: CardModel | null | undefined): boolean {
  return !!card?.isEditableTemplate && !card?.isUserDefined;
}

// A custom (Deck 3) card the user hasn't named yet keeps the placeholder name "blank". In the
// card library/picker it offers Edit + Favourite only — there's nothing to delete from the deck
// yet. Reuses resolveCardVariant so the "custom" definition stays in one place; the same
// isCustom && cardName==="blank" signal drives the unsaved-custom styling in SafetyCard/ActivityCard.
export function isUneditedCustomCard(card: CardModel | null | undefined): boolean {
  if (!card) return false;
  const variant = resolveCardVariant(card, false);
  const isCustom =
    (variant.kind === "safety" || variant.kind === "activity") && variant.isCustom;
  return isCustom && isBlankCardName(card.cardName);
}
